/**
 * router/guard.ts
 * Pure navigation guard — no Vue reactivity, no router instance.
 *
 * resolveNavigation(to, claims) returns a redirect target or true.
 * Being pure (no Vue deps) makes it trivially unit-testable in node.
 *
 * Cross-scope redirect matrix (design §2, spec FS-13):
 *
 * | Route class    | Claims    | Result                              |
 * |----------------|-----------|-------------------------------------|
 * | public         | any       | allow (but bounce authed to home)   |
 * | tenant route   | null      | /login?redirect=<path>              |
 * | tenant route   | tenant    | allow                               |
 * | tenant route   | platform  | /platform/companies                 |
 * | platform route | null      | /platform/login?redirect=<path>     |
 * | platform route | tenant    | /login  (no redirect)               |
 * | platform route | platform  | allow                               |
 */

/** Minimal route descriptor the guard needs — extracted from vue-router RouteLocationNormalized. */
export interface GuardRoute {
  path: string;
  fullPath: string;
  /**
   * `anySession`: opens the same with or without a session, never bounced to a
   * home (the person alta's one-time link — RRHH may open it while logged in).
   */
  meta: { public?: boolean; anySession?: boolean; requiresRole?: string; requiresCapability?: string | readonly string[] };
}

/**
 * Slim claims view — scope discriminant + optional roles/capabilities arrays for tenant sessions.
 * roles is only present (and meaningful) for scope:'tenant'. Platform sessions carry no roles.
 * FS-13.2: requiresRole gate uses the roles array.
 * AOP-7.2: requiresCapability gate uses the capabilities array.
 */
export type GuardClaims =
  | { scope: 'tenant'; roles: string[]; capabilities?: string[] }
  | { scope: 'platform' }
  | null;

export type GuardResult =
  | true
  | { path: string; query?: Record<string, string> };

/**
 * 2026-09-02 — a module's home route ('/servicios', '/compras') requires the
 * module's general "leer" capability, and it's the mandatory first hop every
 * dashboard tile navigates through. A narrow role (e.g. requi.panol: only
 * locx.stock.* + requi.stock.*, added for the pañolero) holds none of that
 * general capability, so it used to get bounced straight back to /dashboard
 * on every click — with no way to ever reach the specific screens it DOES
 * have access to (real prod incident, BRAVA INGENIERIA obras2@ account).
 *
 * Rather than widening the home route's own gate (the home view itself
 * doesn't degrade per-capability — see ServiciosHomeView.vue — so letting a
 * stock-only user in would just 403 on every widget), redirect to the first
 * specific route this user actually holds a capability for, tried in order.
 * Falls through to the normal /dashboard bounce if none match — no
 * regression for a role genuinely unrelated to the module.
 */
const MODULE_HOME_FALLBACK: Readonly<Record<string, readonly { capability: string; path: string }[]>> = {
  '/servicios': [
    // The Depósito is mounted under Servicios too (odd/tasks/compras-deposito.md
    // S1), so a LOCX user stays inside Servicios.
    { capability: 'locx.stock.leer', path: '/servicios/deposito' },
    { capability: 'locx.stock.recepcionar', path: '/servicios/deposito/recibir' },
    { capability: 'locx.stock.despachar', path: '/servicios/deposito/entregar' },
    // locx-permisos-de-trabajo-frontend F1 (task 1.7, design.md D5) — a
    // `firmar`-only account (e.g. a shift signer with no locx.servicio.leer)
    // landing on the module home must reach the permisos screen it DOES hold
    // a capability for, not /dashboard. Same incident class as the
    // requi.panol entries above.
    { capability: 'locx.permiso.firmar', path: '/servicios/permisos' },
  ],
  '/compras': [
    { capability: 'requi.stock.leer', path: '/compras/deposito' },
    { capability: 'locx.stock.leer', path: '/compras/deposito' },
  ],
};

// ---------------------------------------------------------------------------
// Classification helpers (pure)
// ---------------------------------------------------------------------------

function isPlatformRoute(path: string): boolean {
  return path.startsWith('/platform');
}

// ---------------------------------------------------------------------------
// Core guard logic
// ---------------------------------------------------------------------------

/**
 * Resolves the correct navigation outcome for a given destination and session.
 * Pure function: same inputs always produce the same output; no side effects.
 */
export function resolveNavigation(to: GuardRoute, claims: GuardClaims): GuardResult {
  // Public routes: always allowed. If already authenticated, bounce to area home
  // (avoids the re-login loop — FS-13-G, design §2).
  if (to.meta.anySession) return true;
  if (to.meta.public) {
    if (claims?.scope === 'tenant') return { path: '/dashboard' };
    if (claims?.scope === 'platform') return { path: '/platform/companies' };
    return true;
  }

  const isPlat = isPlatformRoute(to.path);

  if (!claims) {
    // Unauthenticated: bounce to the appropriate login with ?redirect preserved.
    if (isPlat) return { path: '/platform/login', query: { redirect: to.fullPath } };
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  if (isPlat) {
    // Platform-area route
    if (claims.scope === 'platform') return true;
    // Tenant token on /platform/* → /login (no redirect — design §2, proposal-locked)
    return { path: '/login' };
  } else {
    // Tenant-area route
    if (claims.scope !== 'tenant') {
      // Platform token on tenant route → send to platform home
      return { path: '/platform/companies' };
    }

    // FS-13.2: optional role gate. meta.requiresRole is set on role-restricted routes.
    // Non-admin users are redirected to /dashboard (cosmetic gate; server is authoritative).
    if (to.meta.requiresRole && !claims.roles.includes(to.meta.requiresRole)) {
      return { path: '/dashboard' };
    }

    // AOP-7.2: optional capability gate. meta.requiresCapability is set on capability-restricted routes.
    // Users lacking the capability are redirected to /dashboard (cosmetic; server is authoritative).
    // Widened to string | readonly string[] (any-of) — 2026-08-31-requi-aprobacion-
    // oc-por-monto, design.md D7: /compras/aprobaciones must stay reachable by a
    // tier-1-only OR tier-2-only comprador, neither of whom holds the single
    // pre-existing requi.caso.aprobar. A single string keeps the exact same
    // one-element behaviour as before this change.
    if (to.meta.requiresCapability) {
      const held = claims.capabilities ?? [];
      const required = Array.isArray(to.meta.requiresCapability)
        ? to.meta.requiresCapability
        : [to.meta.requiresCapability as string];
      const satisfiesOne = (req: string): boolean =>
        held.some((cap) => {
          if (cap === '*') return true;
          if (cap === req) return true;
          if (cap.length > 2 && cap.endsWith(':*') && req.startsWith(cap.slice(0, -1))) return true;
          return false;
        });
      if (!required.some(satisfiesOne)) {
        const fallback = MODULE_HOME_FALLBACK[to.path]?.find((f) => satisfiesOne(f.capability));
        return { path: fallback?.path ?? '/dashboard' };
      }
    }

    return true;
  }
}
